import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import VideoResultPage from './VideoResultPage'
import { generateVideo } from './videoUtils'
import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useAuth } from '../../hooks/useSupabaseAuth.jsx'
import { useSubscription } from '../../hooks/useSubscription'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { AGENT_TYPES } from '../../constants/agentConfig.js'
import AgentSubscriptionWrapper from '../../components/AgentSubscriptionWrapper'

// Video Agent States
const VIDEO_STATES = {
  GENERATING: 'generating',
  COMPLETED: 'completed'
}

const VideoAgentContent = ({ query, onBack, llmService, options }) => {
  const { user } = useAuth()
  const { consumeCredits } = useSubscription()
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const [currentState, setCurrentState] = useState(VIDEO_STATES.GENERATING)
  const [generatedVideo, setGeneratedVideo] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [videoType, setVideoType] = useState(options?.videoType || 'text_to_video')
  const [aspectRatio, setAspectRatio] = useState(options?.aspectRatio || '16:9')
  const [uploadedImage, setUploadedImage] = useState(options?.imageFile || null)
  const hasInitialized = useRef(false)
  const isGenerating = useRef(false)
  const hasCompletedGeneration = useRef(false) // Track if generation has ever completed successfully
  
  // Auto scroll to top when component mounts and set up request ID handler
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Set up global function to update video from request ID
    window.updateVideoFromRequestId = (videoData) => {
      console.log('🔄 Updating video from request ID:', videoData)
      
      // Update the video state with the retrieved data
      setGeneratedVideo({
        ...videoData,
        generatedAt: videoData.generatedAt || new Date().toISOString()
      })
      
      setCurrentState(VIDEO_STATES.COMPLETED)
      setIsLoading(false)
      setError(null)
      hasCompletedGeneration.current = true
      
      console.log('✅ Video berhasil diambil dari request ID')
    }
    
    // Cleanup function
    return () => {
      delete window.updateVideoFromRequestId
    }
  }, [])

  // Check if we already have a result for this query in sessionStorage
  useEffect(() => {
    const imageKey = uploadedImage ? uploadedImage.name || 'image' : 'no-image'
    const sessionKey = `video_result_${query}_${videoType}_${aspectRatio}_${imageKey}`
    const cachedResult = sessionStorage.getItem(sessionKey)
    
    if (cachedResult) {
      try {
        const parsedResult = JSON.parse(cachedResult)
        console.log('📋 Loading cached video result for query:', query)
        setGeneratedVideo(parsedResult)
        setCurrentState(VIDEO_STATES.COMPLETED)
        hasCompletedGeneration.current = true
        setIsLoading(false)
        return
      } catch (error) {
        console.warn('⚠️ Failed to parse cached result, will generate new video')
        sessionStorage.removeItem(sessionKey)
      }
    }
    
    // Only generate if we don't have cached result and haven't initialized yet
    if (query && !hasInitialized.current && !isGenerating.current) {
      hasInitialized.current = true
      console.log('🎬 Starting video generation for query:', query)
      handleStartGeneration()
    }
  }, [query, videoType, aspectRatio, uploadedImage?.name]) // Include all sessionKey dependencies

  const handleStartGeneration = useCallback(async (customVideoType = videoType) => {
    // Prevent multiple simultaneous generations
    if (isGenerating.current) {
      console.log('⚠️ Generation already in progress, skipping...')
      return
    }
    
    // Additional check: if we already have a completed video, don't generate again
    if (currentState === VIDEO_STATES.COMPLETED && generatedVideo) {
      console.log('⚠️ Video already generated and completed, skipping...')
      return
    }
    
    // Strong protection: if generation has ever completed successfully, don't generate again
    if (hasCompletedGeneration.current) {
      console.log('⚠️ Generation has already completed successfully before, skipping...')
      return
    }
    
    console.log('🔄 Starting video generation...')
    
    isGenerating.current = true
    setCurrentState(VIDEO_STATES.GENERATING)
    setIsLoading(true)
    setError(null)
    setGeneratedVideo(null)
    
    try {
      // Check credits before starting generation with timeout and fallback
      let canProceed = true
      try {
        const creditCheckPromise = checkAndWarnCredits(AGENT_TYPES.VIDEO)
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Credit check timeout')), 5000)
        )
        
        canProceed = await Promise.race([creditCheckPromise, timeoutPromise])
      } catch (creditError) {
        console.warn('⚠️ Credit check failed, proceeding with generation:', creditError.message)
        // Continue with generation even if credit check fails
        canProceed = true
      }
      
      if (!canProceed) {
        setIsLoading(false)
        return // Stop if insufficient credits
      }

      console.log(`🔄 Memulai generasi video untuk: ${query}`)
      
      const videoResult = await generateVideo(query, customVideoType, {
        ...options,
        aspectRatio: aspectRatio,
        imageFile: uploadedImage,
        imagePrompt: query
      })
      
      // Gunakan URL langsung dari fal.ai tanpa menyimpan ke Supabase
      console.log('✅ Video berhasil dibuat dari fal.ai')
      setGeneratedVideo(videoResult)
      
      // Cache the result in sessionStorage to prevent regeneration on page revisit
      const imageKey = uploadedImage ? uploadedImage.name || 'image' : 'no-image'
      const sessionKey = `video_result_${query}_${customVideoType}_${aspectRatio}_${imageKey}`
      try {
        sessionStorage.setItem(sessionKey, JSON.stringify(videoResult))
        console.log('💾 Video result cached for future visits')
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache result:', cacheError)
      }
      
      console.log('🎉 Video berhasil dibuat')
      setCurrentState(VIDEO_STATES.COMPLETED)
      hasCompletedGeneration.current = true // Mark as completed to prevent future generations
      
      // Consume credits after successful video generation
      try {
        await consumeCredits(AGENT_TYPES.VIDEO, {
          query: query,
          agentType: AGENT_TYPES.VIDEO,
          agentName: 'Video Agent',
          videoType: customVideoType
        })
        console.log('✅ Credits consumed for Video Agent')
      } catch (creditError) {
        console.error('❌ Failed to consume credits for Video Agent:', creditError)
      }
      
      // Save to agent history
      try {
        await AgentHistoryIntegration.saveVideoResult({
          query: query,
          videos: [videoResult],
          settings: {
            videoType: customVideoType,
            ...options
          }
        })
        console.log('✅ Hasil video berhasil disimpan ke history')
      } catch (historyError) {
        console.error('❌ Error menyimpan ke history:', historyError)
      }
    } catch (err) {
      console.error('❌ Error dalam handleStartGeneration:', err)
      
      // Clear any cached result for this query since generation failed
      const imageKey = uploadedImage ? uploadedImage.name || 'image' : 'no-image'
      const sessionKey = `video_result_${query}_${customVideoType}_${aspectRatio}_${imageKey}`
      try {
        sessionStorage.removeItem(sessionKey)
        console.log('🗑️ Cleared cached result due to generation error')
      } catch (cacheError) {
        console.warn('⚠️ Failed to clear cache after error:', cacheError)
      }
      
      // Provide user-friendly error messages
      let userMessage = err.message
      if (err.message.includes('API Key')) {
        userMessage = 'Konfigurasi API bermasalah. Silakan hubungi administrator.'
      } else if (err.message.includes('timeout')) {
        userMessage = 'Koneksi timeout. Silakan coba lagi dengan koneksi internet yang stabil.'
      } else if (err.message.includes('NetworkError') || err.message.includes('fetch')) {
        userMessage = 'Masalah koneksi internet. Periksa koneksi Anda dan coba lagi.'
      } else if (err.message.includes('400') || err.message.includes('Bad Request')) {
        userMessage = 'Terjadi kesalahan dalam pemrosesan video (Error 400). Silakan coba lagi atau hubungi support jika masalah berlanjut.'
      } else if (err.message.includes('expired') || err.message.includes('URL tidak valid')) {
        userMessage = 'URL video sudah tidak valid. Silakan coba generate ulang video.'
      } else if (err.message.includes('Bucket') || err.message.includes('storage')) {
        userMessage = 'Terjadi masalah dengan penyimpanan video. Video mungkin berhasil dibuat tapi tidak tersimpan permanen.'
      } else if (!err.message || err.message.trim() === '') {
        userMessage = 'Terjadi kesalahan tidak dikenal. Silakan coba lagi.'
      }
      
      setError(userMessage)
      setCurrentState(VIDEO_STATES.GENERATING) // Keep in generating state to allow retry
      hasInitialized.current = false // Reset initialization flag on error to allow retry
    } finally {
      setIsLoading(false)
      isGenerating.current = false // Reset flag when generation completes
    }
  }, [query, checkAndWarnCredits, consumeCredits, llmService, user, videoType, aspectRatio, uploadedImage])

  const handleRegenerateVideo = useCallback((newVideoType) => {
    // Clear cached results for both old and new video types
    const imageKey = uploadedImage ? uploadedImage.name || 'image' : 'no-image'
    const oldSessionKey = `video_result_${query}_${videoType}_${aspectRatio}_${imageKey}`
    const newSessionKey = `video_result_${query}_${newVideoType}_${aspectRatio}_${imageKey}`
    
    try {
      sessionStorage.removeItem(oldSessionKey)
      sessionStorage.removeItem(newSessionKey)
      console.log('🗑️ Cleared cached results for regeneration')
    } catch (error) {
      console.warn('⚠️ Failed to clear cache:', error)
    }
    
    // Reset all generation flags for regeneration
    isGenerating.current = false
    hasCompletedGeneration.current = false // Allow regeneration
    hasInitialized.current = false // Reset initialization flag
    
    setVideoType(newVideoType)
    
    // Force regeneration by resetting state first
    setCurrentState(VIDEO_STATES.GENERATING)
    setGeneratedVideo(null)
    setError(null)
    
    console.log('🔄 Manual regeneration triggered with video type:', newVideoType)
    
    // Call generation with new video type
    handleStartGeneration(newVideoType)
  }, []) // Empty dependency array to prevent unnecessary recreations



  // Always show result page (no outline page needed)
  return (
    <>
      <VideoResultPage
        query={query}
        generatedVideo={generatedVideo}
        isLoading={isLoading}
        error={error}
        isCompleted={currentState === VIDEO_STATES.COMPLETED}
        videoType={videoType}
        aspectRatio={aspectRatio}
        uploadedImage={uploadedImage}
        onBack={onBack}
        onRegenerateVideo={handleRegenerateVideo}
      />
      
      {/* Credit Warning Modal */}
      <CreditWarningModal
        isOpen={warningModal.isOpen}
        onClose={closeWarning}
        actionType={warningModal.actionType}
        creditsRequired={warningModal.creditsRequired}
        creditsRemaining={warningModal.creditsRemaining}
        packageName={warningModal.packageName}
        isFreeUser={warningModal.isFreeUser}
        message={warningModal.message}
      />
    </>
  )
}

// Main VideoAgent component
const VideoAgent = (props) => {
  return (
    <AgentSubscriptionWrapper actionType="video_agent" {...props}>
      <VideoAgentContent {...props} />
    </AgentSubscriptionWrapper>
  )
}

VideoAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

VideoAgentContent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object,
  options: PropTypes.object
}

export default VideoAgent